import { Button, Card, Divider, Flex, Group, Loader, Progress, Space, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp, IconRefresh } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getVotosEmpresaInspiradora, TVoto } from "@/modules/projetos/firestore";
import RelatorioComponent from "@/modules/relatorio/components/relatorio-item/RelatorioComponent";
import useUserStore from "@/modules/users/store";

interface Values {
  projeto: string;
  questionario: string;
}

type AggregatedCompanyVote = {
  name: string;
  votes: number;
  percentageInCategory: number;
};

type AggregatedCategoryVote = {
  category: string;
  totalVotes: number;
  percentage: number;
  companies: AggregatedCompanyVote[];
};

const PERCENTAGE_DECIMAL_PLACES = 2;
const MAX_PERCENTAGE = 100;

type CategoryCardProperties = {
  categoryData: AggregatedCategoryVote;
};

// Constants for styling
const EVEN_ROW_MODULO = 2;

/**
 * Component that displays a category card with voting results
 * @param root0 - The component props
 * @param root0.categoryData - The category data containing voting results
 * @returns The CategoryCard component
 */
function CategoryCard({ categoryData }: CategoryCardProperties) {
  const [opened, { toggle }] = useDisclosure(false);

  return (
    <Card withBorder shadow="sm" p="md">
      <Group justify="space-between" align="center">
        <div style={{ flex: 1 }}>
          <Text fw={500} size="lg">
            {categoryData.category}
          </Text>
          <Text size="sm" c="dimmed">
            {categoryData.totalVotes} votos ({categoryData.percentage.toFixed(PERCENTAGE_DECIMAL_PLACES)}%)
          </Text>
          <Progress value={categoryData.percentage} mt="xs" size="lg" radius="xl" />
        </div>
        <Button
          variant="transparent"
          size="compact-sm"
          rightSection={opened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />}
          onClick={toggle}
          style={{ flexShrink: 0 }}
        >
          Ver mais
        </Button>
      </Group>

      {opened && (
        <>
          <Divider my="md" />
          <Stack gap="xs">
            <Text fw={500} size="sm" mb="xs">
              Empresas mais votadas:
            </Text>
            {categoryData.companies.length > 0 ? (
              categoryData.companies.map((company, index) => (
                <div
                  key={index}
                  style={{
                    padding: "8px",
                    backgroundColor: index % EVEN_ROW_MODULO === 0 ? "var(--mantine-color-gray-0)" : "transparent",
                    borderRadius: "4px"
                  }}
                >
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">{company.name}</Text>
                    <Text size="sm" fw={500}>
                      {company.votes} votos ({company.percentageInCategory.toFixed(PERCENTAGE_DECIMAL_PLACES)}%)
                    </Text>
                  </Group>
                  <Progress value={company.percentageInCategory} size="sm" radius="xl" color="blue" />
                </div>
              ))
            ) : (
              <Text size="sm" c="dimmed">
                Nenhuma empresa votada nesta categoria.
              </Text>
            )}
          </Stack>
        </>
      )}
    </Card>
  );
}

const ResultadoVotacaoPage = () => {
  const [values, setValues] = useState<Values>({ projeto: "", questionario: "" });
  const [votos, setVotos] = useState<TVoto[]>([]);
  const [loading, setLoading] = useState(false);
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const activeProjeto = useUserStore((state) => state.activeProjeto);

  useEffect(() => {
    setValues((previous) => ({ ...previous, questionario: "" }));
  }, [activeProjeto]);

  const fetchVotos = useCallback(async () => {
    if (activeEmpresaId && values.questionario) {
      setLoading(true);
      try {
        const fetchedVotos = await getVotosEmpresaInspiradora(activeEmpresaId, values.questionario);
        setVotos(fetchedVotos);
      } catch (error) {
        console.error("Erro ao buscar votos:", error);
        setVotos([]);
      } finally {
        setLoading(false);
      }
    } else {
      setVotos([]);
    }
  }, [activeEmpresaId, values.questionario]);

  useEffect(() => {
    void fetchVotos();
  }, [fetchVotos]);

  const handleRefresh = useCallback(() => {
    void fetchVotos();
  }, [fetchVotos]);

  const aggregatedVotos = useMemo(() => {
    const totalGlobalVotes = votos.length;
    const categoryMap = new Map<string, { votes: number; companies: Map<string, number> }>();

    for (const voto of votos) {
      if (!categoryMap.has(voto.categoria)) {
        categoryMap.set(voto.categoria, { votes: 0, companies: new Map<string, number>() });
      }
      const categoryData = categoryMap.get(voto.categoria);
      if (categoryData) {
        categoryData.votes++;
        categoryData.companies.set(voto.empresa, (categoryData.companies.get(voto.empresa) || 0) + 1);
      }
    }

    const result: AggregatedCategoryVote[] = [];
    for (const [categoryName, data] of categoryMap.entries()) {
      const companiesList: AggregatedCompanyVote[] = [...data.companies.entries()]
        .map(([companyName, companyVotes]) => ({
          name: companyName,
          votes: companyVotes,
          percentageInCategory: (companyVotes / data.votes) * MAX_PERCENTAGE
        }))
        .sort((a, b) => b.votes - a.votes); // Sort companies by votes descending

      result.push({
        category: categoryName,
        totalVotes: data.votes,
        percentage: (data.votes / totalGlobalVotes) * MAX_PERCENTAGE,
        companies: companiesList
      });
    }

    return result.sort((a, b) => b.totalVotes - a.totalVotes); // Sort categories by total votes descending
  }, [votos]);

  return (
    <>
      <RelatorioComponent setValues={setValues} questionario={values.questionario} />
      <Space h={"xl"} />
      {activeProjeto && values.questionario ? (
        <Card withBorder shadow="md" style={{ flex: 1 }}>
          <Flex justify="space-between" align="center" mb="md">
            <Text fw="bold" c="blue" size="xl">
              Votos por Categoria
            </Text>
            <Button
              leftSection={<IconRefresh size="1rem" />}
              variant="light"
              color="blue"
              size="sm"
              loading={loading}
              onClick={handleRefresh}
            >
              Atualizar
            </Button>
          </Flex>
          {loading ? (
            <Group justify="center" align="center">
              <Text fw="bold" c="blue">
                Carregando votos...
              </Text>
              <Loader size="xs" />
            </Group>
          ) : aggregatedVotos.length > 0 ? (
            <Stack gap="md">
              {aggregatedVotos.map((categoryData, index) => (
                <CategoryCard key={index} categoryData={categoryData} />
              ))}
            </Stack>
          ) : (
            <Text ta="center">Nenhum voto encontrado para este questionário.</Text>
          )}
        </Card>
      ) : (
        <Group justify="center">
          <Text fw={700}>Selecione um projeto e um questionário para visualizar os resultados!</Text>
        </Group>
      )}
      <Space h={"xl"} />
    </>
  );
};

export default ResultadoVotacaoPage;
